import {
  Building2,
  Dna,
  KanbanSquare,
  Radio,
  Send,
  Sparkles,
  Target,
  Workflow,
} from "lucide-react";
import React from "react";
import { PageLayout } from "../components";
import ProductOverview from "../components/product/ProductOverview";
import { useTranslation } from "../contexts/LanguageContext";
import { openLeadB2bSSO } from "../shared/leadB2bSso";

/**
 * Página comercial do Virtus Lead B2B (plataforma separada de prospecção e pipeline B2B).
 * O acesso é por SSO com a sessão do SMM — ver `openLeadB2bSSO()`.
 */
const LeadB2b: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageLayout
      title={t("Virtus Lead B2B")}
      subtitle={t(
        "Prospecção inteligente e pipeline de vendas B2B guiados pelo seu ICP.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
    >
      <ProductOverview
        icon={Target}
        productName={t("Virtus Lead B2B")}
        tagline={t(
          "Encontre as empresas certas, priorize quem tem mais chance de fechar e acompanhe cada negócio até a venda.",
        )}
        description={t(
          "O Virtus Lead B2B parte do DNA da sua marca e do seu cliente ideal (ICP) para prospectar empresas com IA, enriquecer os contatos de decisores e pontuar cada lead pela aderência ao seu perfil. Os leads seguem para um pipeline em Kanban, com follow-ups, cadências de e-mail e disparos de campanha. O acesso é feito com o mesmo login do Social Manager, sem precisar entrar de novo.",
        )}
        ctaLabel={t("Abrir Virtus Lead B2B")}
        onCta={openLeadB2bSSO}
        benefitsTitle={t("Por que usar o Lead B2B")}
        benefitsSubtitle={t("O que ele resolve na sua prospecção")}
        benefits={[
          {
            icon: Sparkles,
            title: t("Prospecção com IA"),
            description: t(
              "Busca empresas por segmento e região e encontra os decisores, sem montar listas na mão.",
            ),
          },
          {
            icon: Target,
            title: t("Priorização por ICP"),
            description: t(
              "Cada lead recebe um score de aderência ao seu cliente ideal, para a equipe focar em quem importa.",
            ),
          },
          {
            icon: KanbanSquare,
            title: t("Pipeline organizado"),
            description: t(
              "Acompanha cada negócio da qualificação à venda, com lembretes de follow-up.",
            ),
          },
        ]}
        featuresTitle={t("Principais recursos")}
        featuresSubtitle={t("O que você encontra dentro do Lead B2B")}
        features={[
          {
            icon: Dna,
            title: t("Brand DNA e ICP"),
            description: t(
              "Proposta de valor, dores e perfil de cliente ideal gerados a partir do site e dos materiais da empresa.",
            ),
          },
          {
            icon: Target,
            title: t("Intelligence Leads"),
            description: t(
              "Prospecção de empresas e contatos com score de aderência e sinais de compra.",
            ),
          },
          {
            icon: Workflow,
            title: t("Cadências e follow-ups"),
            description: t(
              "Sequências de e-mail automáticas e uma central com os follow-ups pendentes.",
            ),
          },
          {
            icon: Send,
            title: t("Campanhas"),
            description: t(
              "Listas de campanha e disparo para os leads selecionados.",
            ),
          },
          {
            icon: Radio,
            title: t("Acesso único (SSO)"),
            description: t(
              "Entra direto com o login do Social Manager, na mesma organização e empresas.",
            ),
          },
        ]}
        flowTitle={t("Como ele se encaixa no seu fluxo")}
        flowSubtitle={t("Do seu cliente ideal até a venda")}
        flow={[
          { icon: Dna, label: t("ICP da sua marca") },
          { icon: Target, label: t("Virtus Lead B2B") },
          { icon: Building2, label: t("Venda fechada") },
        ]}
      />
    </PageLayout>
  );
};

export default LeadB2b;
